
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import { CityStats, Grid, Milestone, ResearchState, BuildingType } from '../types';
import { BUILDINGS } from '../constants';

interface ReportData {
  timestamp: string;
  day: number;
  financials: {
    budget: number;
    debt: number;
    loans: number;
  };
  metrics: {
    compliance: number;
    threat: number;
  };
  infrastructure: Record<string, number>;
  research: {
    completed: string[];
    progress: string;
  };
  milestones: {
    completed: string[];
    pending: string[];
  };
}

export const generateAuditReport = (
  stats: CityStats, 
  grid: Grid, 
  research: ResearchState, 
  milestones: Milestone[]
) => {
  // 1. Compile Infrastructure Counts
  const buildingCounts: Record<string, number> = {};
  grid.flat().forEach(tile => {
    if (tile.buildingType !== BuildingType.None) {
      const name = BUILDINGS[tile.buildingType].name;
      buildingCounts[name] = (buildingCounts[name] || 0) + 1;
    }
  });

  // 2. Structure Data
  const report: ReportData = {
    timestamp: new Date().toISOString(),
    day: stats.day,
    financials: {
      budget: stats.budget,
      debt: stats.loanCount * 1000,
      loans: stats.loanCount
    },
    metrics: {
      compliance: stats.compliance,
      threat: stats.threat
    },
    infrastructure: buildingCounts,
    research: {
      completed: research.completedTechs,
      progress: `${(research.completedTechs.length / 6 * 100).toFixed(1)}% Tree Unlocked`
    },
    milestones: {
      completed: milestones.filter(m => m.completed).map(m => m.name),
      pending: milestones.filter(m => !m.completed).map(m => m.name)
    }
  };

  // 3. Convert to JSON Blob
  const dataStr = JSON.stringify(report, null, 2);
  const blob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  // 4. Trigger Download
  const link = document.createElement('a');
  link.href = url;
  link.download = `Compliance_Audit_Day${stats.day}_${new Date().toISOString().slice(0,10)}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
